// Cost-entries client surface (slice 4e ledger reads + slice 4f link
// surface). Lives in its own sibling module so `manufacturingClient.ts`
// stays under the 400-line file-size-hygiene threshold (precedent:
// receiptsClient, deliveryClient, aliasesClient).
//
// Public surface:
//   - postMaterialCostEntry          -- write a single cost entry (optionally with receipt_id).
//   - listManufacturingInventoryChangelog -- paginated list backing the Inventory Changelog tab.
//   - linkCostEntryToReceipt         -- set or clear receipt_id on an existing entry.
import { manufacturingFetch } from "@/lib/manufacturingClient";
import type {
  MfgCostEntryLinkResponse,
  MfgCostEntryResponse,
  MfgInventoryChangelogFilters,
  MfgInventoryChangelogResponse,
  MfgVendorAliasHint,
} from "@/lib/manufacturing/types";

export function postMaterialCostEntry(payload: {
  material_id: string;
  quantity: number;
  unit_cost: number;
  entry_type: "manual" | "receive" | "adjustment";
  notes: string;
  /** Slice 4f: optional FK to `mfg_inventory_receipts.id`. Set when the
   *  entry is being posted from a receipt's Review modal so the
   *  ledger row + receipt stay linked. */
  receipt_id?: string | null;
  /** Slice 4g: silent learn-on-post payload. Only the Receipt-Review
   *  modal sends this; other cost-entry callers (manual receive lines,
   *  recipe-count, bulk import) omit it and get no alias side-effect. */
  vendor_alias_hint?: MfgVendorAliasHint | null;
}) {
  return manufacturingFetch<MfgCostEntryResponse>("/api/mfg/cost-entry", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/**
 * Inventory changelog -- recent material and recipe cost-entries for the workspace
 * org with material name/code/unit + posted-by display label denormalised
 * onto each row. Used by the Inventory panel's Changelog tab; not
 * admin-gated (it's an operational view, not an audit-log surface).
 */
export function listManufacturingInventoryChangelog(
  filters: MfgInventoryChangelogFilters = {}
) {
  return manufacturingFetch<MfgInventoryChangelogResponse>("/api/mfg/cost-entries", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(filters),
  });
}

/** Slice 4f: set or clear `receipt_id` on an existing cost entry. Pass
 *  `receiptId: null` to clear the link. */
export function linkCostEntryToReceipt(entryId: string, receiptId: string | null) {
  return manufacturingFetch<MfgCostEntryLinkResponse>("/api/mfg/cost-entry/link", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ entry_id: entryId, receipt_id: receiptId }),
  });
}
